import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { useAdminAmdLiveSummary } from '@apps/web/swr_data_fetching/admin_voice_qa_queries';
import { type AmdLiveCall } from '@libraries/typescript/api_client/schema_types';

export const Route = createFileRoute('/_app/admin/amd-live')({
    component: AmdLivePage,
    head: () => ({
        meta: [
            {
                title: 'Live AMD | Phoebe Admin',
            },
        ],
    }),
});

const DAY_OPTIONS = [1, 7, 14, 30] as const;

function recallLabel(caught: number, total: number): string {
    if (total === 0) {
        return '—';
    }
    return `${((caught / total) * 100).toFixed(0)}%`;
}

function StatTile({
    label,
    value,
    hint,
}: {
    readonly label: string;
    readonly value: string;
    readonly hint?: string;
}) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                    {label}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="text-3xl font-semibold">{value}</div>
                {hint ? (
                    <div className="text-muted-foreground mt-1 text-xs">
                        {hint}
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
}

function LiveDisagreementRow({ call }: { readonly call: AmdLiveCall }) {
    return (
        <TableRow>
            <TableCell>{call.organization_name}</TableCell>
            <TableCell className="text-muted-foreground whitespace-nowrap">
                {call.created_at
                    ? new Date(call.created_at).toLocaleString()
                    : '—'}
            </TableCell>
            <TableCell className="tabular-nums">
                {call.score !== null && call.score !== undefined
                    ? call.score.toFixed(3)
                    : '—'}
            </TableCell>
            <TableCell>{call.pivoted ? 'Yes' : 'No'}</TableCell>
            <TableCell>
                {call.llm_marked_voicemail ? 'voicemail' : '—'}
            </TableCell>
            <TableCell>
                <Link
                    className="text-primary underline-offset-2 hover:underline"
                    params={{ callId: call.voice_call_id }}
                    to="/admin/voice-qa/$callId"
                >
                    Open call
                </Link>
            </TableCell>
        </TableRow>
    );
}

function AmdLivePage() {
    const [days, setDays] = useState<number>(7);
    const {
        data: liveSummary,
        isLoading: isLiveLoading,
        error: liveError,
    } = useAdminAmdLiveSummary(days);

    return (
        <div className="mx-auto flex max-w-5xl flex-col gap-6 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold">Live AMD</h1>
                    <p className="text-muted-foreground text-sm">
                        In-call voicemail-detection verdicts on ringed orgs
                        (PHO-13739). Machine verdicts pivot the agent at ~5s.
                    </p>
                </div>
                <div className="flex gap-1">
                    {DAY_OPTIONS.map((option) => (
                        <Button
                            key={option}
                            onClick={() => setDays(option)}
                            size="sm"
                            variant={days === option ? 'default' : 'outline'}
                        >
                            {option}d
                        </Button>
                    ))}
                </div>
            </div>
            <div className="flex flex-col gap-6">
                {isLiveLoading ? (
                    <div className="text-muted-foreground flex items-center gap-2">
                        <Spinner size="md" tone="muted" />
                        Loading live AMD results…
                    </div>
                ) : null}
                {liveError ? (
                    <div className="text-destructive text-sm">
                        Failed to load live AMD results.
                    </div>
                ) : null}
                {liveSummary && liveSummary.total === 0 ? (
                    <p className="text-muted-foreground text-sm">
                        No probed calls in this window — is the ring flag on?
                    </p>
                ) : null}
                {liveSummary && liveSummary.total > 0 ? (
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                            <StatTile
                                label="Probed calls"
                                value={String(liveSummary.total)}
                                hint={
                                    liveSummary.pending_outcome_count > 0
                                        ? `last ${liveSummary.days}d · ${liveSummary.pending_outcome_count} outcomes pending`
                                        : `last ${liveSummary.days}d`
                                }
                            />
                            <StatTile
                                label="Pivot rate"
                                value={`${(
                                    (liveSummary.pivot_count /
                                        liveSummary.total) *
                                    100
                                ).toFixed(1)}%`}
                                hint={`${liveSummary.pivot_count} of ${liveSummary.total}`}
                            />
                            <StatTile
                                label="Machine but live"
                                value={String(
                                    liveSummary.machine_but_live.length,
                                )}
                                hint="possible false pivots — listen"
                            />
                            <StatTile
                                label="Human but voicemail"
                                value={String(
                                    liveSummary.human_but_voicemail.length,
                                )}
                                hint="AMD missed; tool caught"
                            />
                            <StatTile
                                label="AMD caught live"
                                value={recallLabel(
                                    liveSummary.amd_caught_live_count,
                                    liveSummary.voicemail_outcome_count,
                                )}
                                hint={`${liveSummary.amd_caught_live_count} of ${liveSummary.voicemail_outcome_count} voicemails, in-call`}
                            />
                            <StatTile
                                label="LLM caught live"
                                value={recallLabel(
                                    liveSummary.llm_caught_live_count,
                                    liveSummary.voicemail_outcome_count,
                                )}
                                hint={`${liveSummary.llm_caught_live_count} of ${liveSummary.voicemail_outcome_count} · agent alone (mark_as_voicemail)`}
                            />
                        </div>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Machine but live (
                                    {liveSummary.machine_but_live.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {liveSummary.machine_but_live.length === 0 ? (
                                    <p className="text-muted-foreground text-sm">
                                        No machine-but-live calls in this
                                        window.
                                    </p>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>
                                                    Organization
                                                </TableHead>
                                                <TableHead>Time</TableHead>
                                                <TableHead>Score</TableHead>
                                                <TableHead>Pivoted</TableHead>
                                                <TableHead>LLM live</TableHead>
                                                <TableHead />
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {liveSummary.machine_but_live.map(
                                                (call) => (
                                                    <LiveDisagreementRow
                                                        call={call}
                                                        key={call.voice_call_id}
                                                    />
                                                ),
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Human but voicemail (
                                    {liveSummary.human_but_voicemail.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {liveSummary.human_but_voicemail.length ===
                                0 ? (
                                    <p className="text-muted-foreground text-sm">
                                        No human-but-voicemail calls in this
                                        window.
                                    </p>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>
                                                    Organization
                                                </TableHead>
                                                <TableHead>Time</TableHead>
                                                <TableHead>Score</TableHead>
                                                <TableHead>Pivoted</TableHead>
                                                <TableHead>LLM live</TableHead>
                                                <TableHead />
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {liveSummary.human_but_voicemail.map(
                                                (call) => (
                                                    <LiveDisagreementRow
                                                        call={call}
                                                        key={call.voice_call_id}
                                                    />
                                                ),
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
